import { RedoOutlined } from "@ant-design/icons";
import { useIsFetching, useQuery } from "@tanstack/react-query";
import { Space, Button } from "antd";
import dayjs from "dayjs";
import { RangeValue } from "./ChartContainer";
import React, { useCallback, useState } from "react";
import { binDBKeys } from "src/pages/binOverview/BinCommander";
import { ChartDatePicker } from "./Co2Chart";
import { RangeFinderChart, useGrainHeightHistoryQueryOptions, useGrainHeightHistoryUserEnteredQueryOptions } from "./RangeFinderChart";
import { BaseChartProps } from "./TemperatureCableChart";
import { BaseChartQueryOptions, BaseCsv, useAddDefaultAnnotationsToCsv } from "./modehistoryAndFanChangeHistory";
import { rangeFinderBushelsHeader, rangeFinderPercentFullHeader, timestampHeader, userBushelsHeader, userPercentFullHeaer } from "./csv/core";
import { sortedLastIndexBy } from "lodash";
import { CsvButton } from "./csv/CsvButton";
import { CSVLink } from "react-csv";
import { BaseHeaders } from "./csv/types";



export const FillLevelCsv = (props: BaseChartQueryOptions) => {
    const rangeFinderQuery = useQuery({
        ...useGrainHeightHistoryQueryOptions({
            binId: props.binId,
            range: props.range,
        }),
    });

    const userLoadsQuery = useQuery({
        ...useGrainHeightHistoryUserEnteredQueryOptions({
            binId: props.binId,
            range: props.range,
        }),
    });



    function flattenToCsv() {

        const headers: BaseHeaders = [timestampHeader, rangeFinderPercentFullHeader, rangeFinderBushelsHeader, userBushelsHeader, userPercentFullHeaer];

        const data: BaseCsv[] = [];

        rangeFinderQuery.data?.forEach((row, i) => {
            const newRow: BaseCsv = {
                [timestampHeader]: row.x,
                [rangeFinderPercentFullHeader]: row.percentFullCalculated,
                [rangeFinderBushelsHeader]: row.bushelsFull,
            };

            const closestUserLoadIndex = sortedLastIndexBy(userLoadsQuery.data, row as {x: string}, (row) => row.x);
            const getClosestLoadFromNearestIndex = () => {
                if ((userLoadsQuery?.data?.length ?? 0) < 1) {
                    return null;
                }
                return userLoadsQuery?.data?.[closestUserLoadIndex - 1];
            }

            const closestUserLoad = getClosestLoadFromNearestIndex();

            // @ts-ignore
            const percentFullUser = closestUserLoad?.percentFullUser;
            // @ts-ignore
            const bushelsFullUser = closestUserLoad?.bushelsFullUser;

            newRow[userBushelsHeader] = bushelsFullUser;
            newRow[userPercentFullHeaer] = percentFullUser;

            data.push(newRow);
        });



        return {
            headers: headers, data: data
        };
    }
    const data = flattenToCsv();

    const finalCsv = useAddDefaultAnnotationsToCsv({
        baseData: data.data,
        baseHeaders: data.headers,
        binId: props.binId, range: props.range,
    });

    return <CsvButton data={finalCsv.data} headers={finalCsv.headers} baseFileName="fill-level" />
    

}

export const FillLevelChartTab = (props: BaseChartProps) => {


    const currentDate = dayjs();
    const priorDate = currentDate.subtract(7, 'days');


    const isControlled = typeof props.value != 'undefined';
    const [internalValue, setIntervalValue] = useState<RangeValue>([priorDate, currentDate]);
    const inprogress = useIsFetching([...binDBKeys.bin(props.binId), "charts"]);

    // Internally, we need to deal with some value. Depending on whether
    // the component is controlled or not, that value comes from its
    // props or from its internal state.

    const value = isControlled ? props.value : internalValue;


    const onDateChange = useCallback((values: RangeValue, formatter?: any) => {
        if (props.onChange) {
            props.onChange(values);
        }
        else {
            setIntervalValue(values);
        }
    }, [props.onChange]);

    return <>
        <div>
            <Space direction="horizontal" wrap>
                <ChartDatePicker startDate={value?.[0]!} endDate={value?.[1]!} onDateRangeChange={onDateChange} />
                <Button type="primary" disabled={inprogress > 0} icon={<RedoOutlined spin={inprogress > 0} />} onClick={() => {
                    onDateChange([value?.[0]!, dayjs()], ['', '']);
                }}>
                </Button>
                <FillLevelCsv binId={props.binId} range={value} />

            </Space>

            <div style={{ width: "100%" }}>
                <RangeFinderChart binId={props.binId} deviceId={props.deviceId} onChange={onDateChange} value={value} />
            </div>
        </div>
    </>
}